/**
 * When Supabase insert fails during batch scan, queue rows locally and retry in the background.
 */

const STORAGE_KEY = "ed_batch_scan_pending_v1";

const BROADCAST_CHANNEL_NAME = "ed-batch-scan-pending-v1";

/** Drop unsynced rows older than this so localStorage cannot fill with stale retries. */
const MAX_PENDING_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Cap unique pending lines so localStorage and flush work stay bounded. */
/** Must cover large batch runs when the device is offline or inserts fail — aligns with pro monthly batch cap. */
export const MAX_BATCH_SCAN_PENDING_QUEUE = 800;

const INSERT_ATTEMPTS = 3;
const INSERT_BACKOFF_MS = [0, 600, 1400];

let broadcastChannelSingleton: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    if (!broadcastChannelSingleton) broadcastChannelSingleton = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    return broadcastChannelSingleton;
  } catch {
    return null;
  }
}

/** Notifies this tab and other tabs that pending queue storage may have changed. */
export function notifyBatchPendingChanged(): void {
  if (typeof window === "undefined") return;
  try {
    getBroadcastChannel()?.postMessage({ type: "pending-changed" });
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent("ed-batch-pending-changed"));
  } catch {
    /* ignore */
  }
}

export type PendingBatchMedicationRow = {
  user_id: string;
  name: string;
  dosage: string;
  quantity: number;
  unit: string;
  expiry_month: number;
  expiry_year: number;
  price_per_box: number | null;
  status: string | null;
  /** Matches `medications.product_category` when batch save queues an offline retry. */
  product_category?: string | null;
  supplier_name?: string | null;
  scan_date: string;
  incomplete_core_fields?: string[];
};

export type PendingBatchSave = {
  id: string;
  /** Same id as the optimistic row in client state until server row replaces it */
  clientMedId: string;
  row: PendingBatchMedicationRow;
  createdAt: number;
};

function readAll(): PendingBatchSave[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is PendingBatchSave =>
        x &&
        typeof x === "object" &&
        typeof (x as PendingBatchSave).id === "string" &&
        typeof (x as PendingBatchSave).clientMedId === "string" &&
        typeof (x as PendingBatchSave).createdAt === "number" &&
        (x as PendingBatchSave).row &&
        typeof (x as PendingBatchSave).row === "object",
    );
  } catch {
    return [];
  }
}

function writeAll(items: PendingBatchSave[]): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch {
    return false;
  }
}

export type EnqueueBatchResult = { ok: true } | { ok: false; reason: "quota" | "max_queue" };

/**
 * Queue a failed insert for retry. Replaces any existing entry with the same `clientMedId` (idempotent for that id).
 */
export function enqueueBatchScanPending(row: PendingBatchMedicationRow, clientMedId: string): EnqueueBatchResult {
  const list = readAll();
  const withoutDup = list.filter((p) => p.clientMedId !== clientMedId);
  if (withoutDup.length >= MAX_BATCH_SCAN_PENDING_QUEUE) {
    return { ok: false, reason: "max_queue" };
  }
  const next: PendingBatchSave[] = [
    ...withoutDup,
    {
      id: `bq-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      clientMedId,
      row: { ...row },
      createdAt: Date.now(),
    },
  ];
  if (!writeAll(next)) {
    return { ok: false, reason: "quota" };
  }
  notifyBatchPendingChanged();
  return { ok: true };
}

/** Current pending count after pruning stale rows (persists prune without broadcasting — subscribers refresh separately). */
export function getBatchScanPendingCount(): number {
  if (typeof window === "undefined") return 0;
  const raw = readAll();
  const list = pruneStalePending(raw);
  if (list.length !== raw.length) {
    writeAll(list);
  }
  return list.length;
}

/** Subscribe to pending count changes (same tab, other tabs via `storage`, and BroadcastChannel). */
export function subscribeBatchPendingCount(onChange: (count: number) => void): () => void {
  if (typeof window === "undefined") {
    onChange(0);
    return () => {};
  }
  const update = () => {
    onChange(getBatchScanPendingCount());
  };
  update();
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) update();
  };
  const onCustom = () => update();
  window.addEventListener("storage", onStorage);
  window.addEventListener("ed-batch-pending-changed", onCustom);
  let ch: BroadcastChannel | null = null;
  try {
    ch = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(BROADCAST_CHANNEL_NAME) : null;
    if (ch) ch.onmessage = () => update();
  } catch {
    ch = null;
  }
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("ed-batch-pending-changed", onCustom);
    ch?.close();
  };
}

type SupabaseLike = {
  from: (t: string) => {
    insert: (rows: PendingBatchMedicationRow[]) => {
      select: (cols: string) => {
        single: () => Promise<{ data: unknown; error: { message?: string } | null }>;
      };
    };
  };
};

function pruneStalePending(list: PendingBatchSave[]): PendingBatchSave[] {
  const now = Date.now();
  return list.filter((p) => now - p.createdAt <= MAX_PENDING_AGE_MS);
}

async function insertWithRetries(
  supabase: SupabaseLike,
  row: PendingBatchMedicationRow,
): Promise<{ data: unknown; error: { message?: string } | null }> {
  let last: { data: unknown; error: { message?: string } | null } = {
    data: null,
    error: { message: "No attempts" },
  };
  for (let a = 0; a < INSERT_ATTEMPTS; a++) {
    const delay = INSERT_BACKOFF_MS[a] ?? 0;
    if (delay > 0) {
      await new Promise<void>((r) => setTimeout(r, delay));
    }
    last = await supabase.from("medications").insert([row]).select("*").single();
    if (!last.error && last.data) return last;
  }
  return last;
}

export async function flushBatchScanPendingQueue(
  supabase: SupabaseLike,
  onInserted: (data: unknown, clientMedId: string) => void,
): Promise<{ flushed: number; remaining: number }> {
  const raw = readAll();
  const list = pruneStalePending(raw);
  let storageTouched = false;
  if (list.length !== raw.length) {
    writeAll(list);
    storageTouched = true;
  }
  if (list.length === 0) {
    if (storageTouched) notifyBatchPendingChanged();
    return { flushed: 0, remaining: 0 };
  }
  const remaining: PendingBatchSave[] = [];
  let flushed = 0;
  for (let i = 0; i < list.length; i++) {
    const p = list[i];
    if (i > 0) {
      await new Promise<void>((r) => queueMicrotask(r));
    }
    const { data, error } = await insertWithRetries(supabase, p.row);
    if (error || !data) {
      remaining.push(p);
      continue;
    }
    flushed += 1;
    onInserted(data, p.clientMedId);
  }
  writeAll(remaining);
  notifyBatchPendingChanged();
  return { flushed, remaining: remaining.length };
}
